import { computed, ref } from 'vue';
import axios from 'axios';

import { lang } from '@/lang/composable';
import { useLayout } from '@src/Common/Composables/useLayout';
import type { ConversationSummary, DrawerMessage, Grounded } from '@src/Advisor/Pages/types';

const busy = ref(false);
const conversationId = ref<number | null>(null);
const messages = ref<DrawerMessage[]>([]);
const conversations = ref<ConversationSummary[]>([]);
const historyOpen = ref(false);
const loadingHistory = ref(false);

let historyPatched = false;

/**
 * The admin drawer always talks to the universal 'admin' agent; module
 * fragments extend it server-side.
 */
const AGENT = 'admin';

/** Screen tokens are a planning-drawer feature; the admin drawer strips them. */
function stripScreenTokens(html: string): string {
    return html.replace(/\[\[screen:[^\]]+\]\]/g, '');
}

/** Headers for the SSE fetch — the web middleware accepts the XSRF cookie header. */
function streamHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Requested-With': 'XMLHttpRequest',
    };

    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);

    if (match) {
        headers['X-XSRF-TOKEN'] = decodeURIComponent(match[1]);
    }

    return headers;
}

/* eslint-disable no-await-in-loop */
/** Parse an SSE byte stream into JSON payloads, ending on [DONE]. */
async function* sseEvents(body: ReadableStream<Uint8Array>): AsyncGenerator<Record<string, any>> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    for (;;) {
        const { done, value } = await reader.read();

        if (done) {
            return;
        }

        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n\n');

        while (boundary >= 0) {
            const frame = buffer.slice(0, boundary).trim();
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf('\n\n');

            if (!frame.startsWith('data: ')) {
                continue;
            }

            const data = frame.slice(6);

            if (data === '[DONE]') {
                return;
            }

            try {
                yield JSON.parse(data);
            } catch {
                // Ignore malformed frames; the terminator or complete event still ends the stream.
            }
        }
    }
}
/* eslint-enable no-await-in-loop */

/**
 * Tab switches on admin edit screens are bare history.pushState calls (no
 * Inertia visit), so patch the history API once to broadcast a window event
 * the drawer can re-match on. popstate covers back/forward.
 */
function patchHistoryOnce(): void {
    if (historyPatched || typeof window === 'undefined') {
        return;
    }

    historyPatched = true;

    for (const method of ['pushState', 'replaceState'] as const) {
        const original = window.history[method].bind(window.history);

        window.history[method] = (...args: Parameters<History['pushState']>) => {
            original(...args);
            window.dispatchEvent(new Event('advisor:locationchange'));
        };
    }
}

/**
 * Singleton drawer state — survives Inertia navigation. Open/closed state
 * itself lives in useLayout('drawer.right') as the named panel id 'advisor'
 * (shared drawer slot contract; 'comments' belongs to Entries).
 */
export function useAdvisorDrawer() {
    const { change, get } = useLayout();

    const opened = computed(() => get('drawer.right') === 'advisor');

    function open(): void {
        change('drawer.right', 'advisor');
    }

    function close(): void {
        change('drawer.right', false);
    }

    function toggle(): void {
        if (opened.value) {
            close();

            return;
        }

        open();
    }

    /** Start re-matching on history API navigation (pushState tab switches, back/forward). */
    function installHistoryListener(handler: () => void): void {
        patchHistoryOnce();
        window.addEventListener('advisor:locationchange', handler);
        window.addEventListener('popstate', handler);
    }

    /** Stop listening to history API navigation. */
    function removeHistoryListener(handler: () => void): void {
        window.removeEventListener('advisor:locationchange', handler);
        window.removeEventListener('popstate', handler);
    }

    /** One conversation turn against the universal admin agent, streamed over SSE. */
    function send(message: string): void {
        if (busy.value || !message.trim()) {
            return;
        }

        busy.value = true;
        messages.value.push({ role: 'user', text: message });

        sendStreaming(message).finally(() => {
            busy.value = false;
        });
    }

    /** Streamed turn; falls back to the blocking endpoint before the first delta. */
    async function sendStreaming(message: string): Promise<void> {
        const placeholder: DrawerMessage = { role: 'assistant', text: '', streaming: true };
        messages.value.push(placeholder);
        const live = messages.value[messages.value.length - 1];

        let sawDelta = false;

        try {
            const res = await fetch(route('advisor.converse.stream'), {
                method: 'POST',
                credentials: 'same-origin',
                headers: streamHeaders(),
                body: JSON.stringify({
                    agent: AGENT,
                    message,
                    conversation_id: conversationId.value ?? undefined,
                }),
            });

            if (!res.ok || !res.body || !(res.headers.get('content-type') ?? '').includes('text/event-stream')) {
                messages.value.pop();

                return await sendBlocking(message, res.status);
            }

            for await (const payload of sseEvents(res.body)) {
                if (payload.type === 'delta') {
                    sawDelta = true;
                    live.text += payload.text ?? '';
                } else if (payload.type === 'complete') {
                    conversationId.value = payload.conversation_id ?? conversationId.value;
                    live.text = payload.reply ?? live.text;
                    live.html = stripScreenTokens(payload.reply_html ?? '');
                    live.grounded = (payload.grounded ?? []) as Grounded[];
                } else if (payload.type === 'error') {
                    live.text = payload.error ?? lang('advisor::advisor.errors.not_found');
                }
            }

            live.streaming = false;
        } catch {
            if (sawDelta) {
                live.streaming = false;
                live.text = live.text || lang('advisor::advisor.errors.not_found');

                return;
            }

            messages.value.pop();

            return sendBlocking(message);
        }
    }

    /** Blocking JSON turn — fallback when streaming is unavailable. */
    function sendBlocking(message: string, failedStatus?: number): Promise<void> {
        if (failedStatus === 429) {
            messages.value.push({ role: 'assistant', text: lang('advisor::advisor.errors.rate_limited') });

            return Promise.resolve();
        }

        return axios
            .post(route('advisor.converse'), {
                agent: AGENT,
                message,
                conversation_id: conversationId.value ?? undefined,
            })
            .then(({ data }) => {
                conversationId.value = data.conversation_id ?? conversationId.value;
                messages.value.push({
                    role: 'assistant',
                    text: data.reply ?? '',
                    html: stripScreenTokens(data.reply_html ?? ''),
                    grounded: (data.grounded ?? []) as Grounded[],
                });
            })
            .catch((err) => {
                const rateLimited = err?.response?.status === 429;

                messages.value.push({
                    role: 'assistant',
                    text: rateLimited
                        ? lang('advisor::advisor.errors.rate_limited')
                        : (err?.response?.data?.error ?? ''),
                });
            });
    }

    /** Confirmed proposal — server-side re-validation happens in the skill's commit(). */
    function commit(skill: string, payload: Record<string, unknown>): Promise<unknown> {
        return axios.post(route('advisor.commit'), { skill, payload }).then(({ data }) => data);
    }

    /** Forget the running conversation and start fresh. */
    function reset(): void {
        conversationId.value = null;
        messages.value = [];
        historyOpen.value = false;
    }

    /** Toggle the history panel, loading the recent conversations on open. */
    function toggleHistory(): void {
        historyOpen.value = !historyOpen.value;

        if (historyOpen.value) {
            loadConversations();
        }
    }

    /** Refresh the caller's recent conversations for the admin agent. */
    function loadConversations(): void {
        loadingHistory.value = true;

        axios
            .get(route('advisor.conversations.index'), { params: { agent: AGENT } })
            .then(({ data }) => {
                conversations.value = (data.conversations ?? []) as ConversationSummary[];
            })
            .catch(() => {
                conversations.value = [];
            })
            .finally(() => {
                loadingHistory.value = false;
            });
    }

    /** Rehydrate a stored conversation into the message stream and continue it. */
    function openConversation(id: number): void {
        if (busy.value) {
            return;
        }

        busy.value = true;

        axios
            .get(route('advisor.conversations.show', { id }), { params: { agent: AGENT } })
            .then(({ data }) => {
                const rehydrated: DrawerMessage[] = [];

                for (const turn of data.turns ?? []) {
                    rehydrated.push({ role: 'user', text: turn.prompt ?? '', historical: true });

                    if (!turn.reply_html && !(turn.grounded ?? []).length) {
                        continue;
                    }

                    rehydrated.push({
                        role: 'assistant',
                        text: '',
                        html: stripScreenTokens(turn.reply_html ?? ''),
                        grounded: (turn.grounded ?? []) as Grounded[],
                        historical: true,
                    });
                }

                conversationId.value = data.id;
                messages.value = rehydrated;
                historyOpen.value = false;
            })
            .finally(() => {
                busy.value = false;
            });
    }

    return {
        opened,
        busy,
        conversationId,
        messages,
        conversations,
        historyOpen,
        loadingHistory,
        open,
        close,
        toggle,
        send,
        commit,
        reset,
        toggleHistory,
        loadConversations,
        openConversation,
        installHistoryListener,
        removeHistoryListener,
    };
}
